<script setup lang="ts">
// The frame every workbench card shares: a clickable Bootstrap card that reads
// as one destination. The title carries the link and Bootstrap's stretched-link
// spreads its hit area over the whole card, so the arrow is an affordance
// rather than a target of its own — and a card that also needs a button of its
// own (Communications' "Take a call") can carry one without nesting links.
// Callers supply the title and the body; the frame supplies equal height and
// the hover lift.
import type { RouteLocationRaw } from "vue-router";

defineProps<{
	title: string;
	subtitle?: string;
	to: RouteLocationRaw;
}>();
</script>

<template>
	<div class="card h-100 home-card position-relative">
		<div class="card-body d-flex flex-column p-2">
			<div class="d-flex align-items-baseline justify-content-between gap-1">
				<h6 class="card-title fw-bold mb-0 text-body">
					<RouterLink
						class="stretched-link text-decoration-none text-reset"
						:to="to"
					>
						{{ title }}
					</RouterLink>
				</h6>
				<i class="bi bi-arrow-right home-card-arrow" aria-hidden="true"></i>
			</div>
			<p v-if="subtitle" class="text-muted small mb-2">{{ subtitle }}</p>
			<div class="flex-grow-1">
				<slot />
			</div>
			<div v-if="$slots.footer" class="mt-2 pt-1 border-top">
				<slot name="footer" />
			</div>
		</div>
	</div>
</template>

<style scoped>
.home-card {
	color: inherit;
	transition:
		box-shadow 0.15s ease,
		transform 0.15s ease;
}

.home-card:hover {
	box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
	transform: translateY(-2px);
}

/* The stretched link is a full-card overlay, so anything a card puts in front
 * of it has to be lifted above it to stay clickable. */
.home-card :deep(.home-card-action) {
	position: relative;
	z-index: 2;
}

.home-card-arrow {
	color: var(--bs-primary);
	font-size: 1.1rem;
}
</style>
